const flagManifest = require('../assets/flags/manifest.json') as {
    flags: Record<string, string>
}

const COMMONWEALTH_GAMES_COUNTRY_NAMES: Record<string, string> = {
    AIA: 'Anguilla',
    ANT: 'Antigua and Barbuda',
    AUS: 'Australia',
    BAN: 'Bangladesh',
    BAR: 'Barbados',
    BIZ: 'Belize',
    BER: 'Bermuda',
    BOT: 'Botswana',
    IVB: 'British Virgin Is.',
    BRU: 'Brunei Darussalam',
    CMR: 'Cameroon',
    CAN: 'Canada',
    CAY: 'Cayman Islands',
    COK: 'Cook Islands',
    CYP: 'Cyprus',
    DMA: 'Dominica',
    ENG: 'England',
    SWZ: 'Eswatini',
    FLK: 'Falkland Islands',
    FIJ: 'Fiji',
    GAB: 'Gabon',
    GHA: 'Ghana',
    GIB: 'Gibraltar',
    GRN: 'Grenada',
    GGY: 'Guernsey',
    GUY: 'Guyana',
    IND: 'India',
    IOM: 'Isle of Man',
    JAM: 'Jamaica',
    JEY: 'Jersey',
    KEN: 'Kenya',
    KIR: 'Kiribati',
    LES: 'Lesotho',
    MAW: 'Malawi',
    MAS: 'Malaysia',
    MDV: 'Maldives',
    MLT: 'Malta',
    MRI: 'Mauritius',
    MSR: 'Montserrat',
    MOZ: 'Mozambique',
    NAM: 'Namibia',
    NRU: 'Nauru',
    NZL: 'New Zealand',
    NGR: 'Nigeria',
    NIU: 'Niue',
    NFK: 'Norfolk Island',
    NIR: 'Northern Ireland',
    PAK: 'Pakistan',
    PNG: 'Papua New Guinea',
    RWA: 'Rwanda',
    LCA: 'Saint Lucia',
    SAM: 'Samoa',
    SCO: 'Scotland',
    SEY: 'Seychelles',
    SLE: 'Sierra Leone',
    SGP: 'Singapore',
    SOL: 'Solomon Islands',
    RSA: 'South Africa',
    SRI: 'Sri Lanka',
    SHN: 'St Helena',
    SKN: 'St Kitts and Nevis',
    SVG: 'StVincent&Grenadines',
    TAN: 'Tanzania',
    BAH: 'The Bahamas',
    GAM: 'The Gambia',
    TOG: 'Togo',
    TGA: 'Tonga',
    TTO: 'Trinidad and Tobago',
    TCA: 'Turks & Caicos Is.',
    TUV: 'Tuvalu',
    UGA: 'Uganda',
    VAN: 'Vanuatu',
    WAL: 'Wales',
    ZAM: 'Zambia',
}

interface MedalNumbers {
    Type: string
    Gold: number | null
    Silver: number | null
    Bronze: number | null
    Total: number | null
}

interface MedalTallyEntry {
    SortRank: number | null
    Rank: number | null
    SortRankTotal: number | null
    RankTotal: number | null
    OrganisationCode: string | null
    MedalNumbers: MedalNumbers[]
}

interface MedalTallyApiResponse {
    Data: MedalTallyEntry[]
}

interface MicroplusMedalTallyEntry {
    organisationCode: string | null
    organisationDescription: string | null
    rank: number | null
    sortRank: number | null
    rankTotal: number | null
    sortRankTotal: number | null
    gold: number | null
    silver: number | null
    bronze: number | null
    total: number | null
}

interface MicroplusMedalTallyApiResponse {
    entries: MicroplusMedalTallyEntry[]
}

export interface CommonwealthGamesMedalTallyRow {
    goldRank: number | null
    totalRank: number | null
    organisationCode: string
    countryName: string
    flagSrc: string | null
    gold: number
    silver: number
    bronze: number
    total: number
}

export type CommonwealthGamesMedalTallySortKey =
    | 'country'
    | 'gold'
    | 'silver'
    | 'bronze'
    | 'total'

export type CommonwealthGamesMedalTallySortDirection = 'asc' | 'desc'

export interface CommonwealthGamesMedalTallyDisplayRow
    extends CommonwealthGamesMedalTallyRow {
    displayRank: number | null
}

interface SortContext {
    rowsCount: number
}

interface NormalizedMedalTallyEntry {
    organisationCode: string | null
    sortRank: number | null
    rank: number | null
    sortRankTotal: number | null
    rankTotal: number | null
    gold: number | null
    silver: number | null
    bronze: number | null
    total: number | null
}

const toNumber = (value: number | null | undefined): number =>
    typeof value === 'number' ? value : 0

const getFlagSrc = (organisationCode: string): string | null => {
    const relativeFlagPath = flagManifest.flags[organisationCode]

    if (!relativeFlagPath) {
        return null
    }

    const flagAsset = require(`../assets/flags/${relativeFlagPath.replace(
        './',
        '',
    )}`)

    return typeof flagAsset === 'string' ? flagAsset : flagAsset.default
}

export const mapMedalTallyResponse = (
    responseBody: string,
): CommonwealthGamesMedalTallyRow[] => {
    const parsedResponse = JSON.parse(responseBody) as
        | MedalTallyApiResponse
        | MicroplusMedalTallyApiResponse

    const entries = getNormalizedEntries(parsedResponse)

    if (entries.length === 0) {
        return []
    }

    return entries.map((entry) => {
        const isSummaryRow = !entry.organisationCode
        const organisationCode = entry.organisationCode ?? 'TOTAL'

        return {
            goldRank: isSummaryRow ? null : entry.sortRank ?? entry.rank ?? null,
            totalRank: isSummaryRow
                ? null
                : entry.sortRankTotal ?? entry.rankTotal ?? null,
            organisationCode,
            countryName: isSummaryRow
                ? 'TOTAL'
                : COMMONWEALTH_GAMES_COUNTRY_NAMES[organisationCode] ??
                  organisationCode,
            flagSrc: isSummaryRow ? null : getFlagSrc(organisationCode),
            gold: toNumber(entry.gold),
            silver: toNumber(entry.silver),
            bronze: toNumber(entry.bronze),
            total: toNumber(entry.total),
        }
    })
}

const getNormalizedEntries = (
    response: MedalTallyApiResponse | MicroplusMedalTallyApiResponse,
): NormalizedMedalTallyEntry[] => {
    if (Array.isArray((response as MicroplusMedalTallyApiResponse).entries)) {
        return (response as MicroplusMedalTallyApiResponse).entries.map(
            (entry) => ({
                organisationCode: entry.organisationCode || null,
                sortRank: entry.sortRank,
                rank: entry.rank,
                sortRankTotal: entry.sortRankTotal,
                rankTotal: entry.rankTotal,
                gold: entry.gold,
                silver: entry.silver,
                bronze: entry.bronze,
                total: entry.total,
            }),
        )
    }

    if (Array.isArray((response as MedalTallyApiResponse).Data)) {
        return (response as MedalTallyApiResponse).Data.map((entry) => {
            const totals =
                entry.MedalNumbers.find(({ Type }) => Type === 'TOT') ??
                entry.MedalNumbers[0]

            return {
                organisationCode: entry.OrganisationCode,
                sortRank: entry.SortRank,
                rank: entry.Rank,
                sortRankTotal: entry.SortRankTotal,
                rankTotal: entry.RankTotal,
                gold: totals?.Gold ?? null,
                silver: totals?.Silver ?? null,
                bronze: totals?.Bronze ?? null,
                total: totals?.Total ?? null,
            }
        })
    }

    return []
}

const getMetricValue = (
    row: CommonwealthGamesMedalTallyRow,
    sortKey: Exclude<CommonwealthGamesMedalTallySortKey, 'country'>,
) => {
    switch (sortKey) {
        case 'silver':
            return row.silver
        case 'bronze':
            return row.bronze
        case 'total':
            return row.total
        case 'gold':
        default:
            return row.gold
    }
}

const getGoldRankValue = (row: CommonwealthGamesMedalTallyRow) =>
    row.goldRank ?? Number.MAX_SAFE_INTEGER

const getTotalRankValue = (row: CommonwealthGamesMedalTallyRow) =>
    row.totalRank ?? Number.MAX_SAFE_INTEGER

const applyDirection = (
    value: number,
    sortDirection: CommonwealthGamesMedalTallySortDirection,
) => (sortDirection === 'asc' ? value * -1 : value)

interface SortDefinition {
    compare: (
        left: CommonwealthGamesMedalTallyRow,
        right: CommonwealthGamesMedalTallyRow,
        sortDirection: CommonwealthGamesMedalTallySortDirection,
    ) => number
    getDisplayRank: (
        row: CommonwealthGamesMedalTallyRow,
        rowIndex: number,
        sortDirection: CommonwealthGamesMedalTallySortDirection,
        context: SortContext,
    ) => number | null
}

const medalSortDefinition: SortDefinition = {
    compare: (left, right, sortDirection) =>
        applyDirection(
            getGoldRankValue(left) - getGoldRankValue(right),
            sortDirection,
        ),
    getDisplayRank: (row, rowIndex, sortDirection, context) =>
        sortDirection === 'asc'
            ? context.rowsCount - rowIndex
            : row.goldRank,
}

const SORT_DEFINITIONS: Record<CommonwealthGamesMedalTallySortKey, SortDefinition> =
    {
        country: {
            compare: (left, right, sortDirection) => {
                const countryDifference = left.countryName.localeCompare(
                    right.countryName,
                )

                return sortDirection === 'asc'
                    ? countryDifference
                    : countryDifference * -1
            },
            getDisplayRank: (_row, rowIndex) => rowIndex + 1,
        },
        gold: medalSortDefinition,
        silver: {
            compare: (left, right, sortDirection) => {
                const scoreDifference = right.silver - left.silver

                if (scoreDifference !== 0) {
                    return applyDirection(scoreDifference, sortDirection)
                }

                return medalSortDefinition.compare(left, right, sortDirection)
            },
            getDisplayRank: (_row, rowIndex) => rowIndex + 1,
        },
        bronze: {
            compare: (left, right, sortDirection) => {
                const scoreDifference = right.bronze - left.bronze

                if (scoreDifference !== 0) {
                    return applyDirection(scoreDifference, sortDirection)
                }

                return medalSortDefinition.compare(left, right, sortDirection)
            },
            getDisplayRank: (_row, rowIndex) => rowIndex + 1,
        },
        total: {
            compare: (left, right, sortDirection) =>
                applyDirection(
                    getTotalRankValue(left) - getTotalRankValue(right),
                    sortDirection,
                ),
            getDisplayRank: (row, rowIndex, sortDirection, context) =>
                sortDirection === 'asc'
                    ? context.rowsCount - rowIndex
                    : row.totalRank,
        },
    }

export const getSortedMedalTallyRows = (
    rows: CommonwealthGamesMedalTallyRow[],
    sortBy: CommonwealthGamesMedalTallySortKey,
    sortDirection: CommonwealthGamesMedalTallySortDirection = 'desc',
): CommonwealthGamesMedalTallyDisplayRow[] => {
    const summaryRow = rows.find((row) => row.goldRank === null) ?? null
    const rankingRows = rows.filter((row) => row.goldRank !== null)
    const sortDefinition = SORT_DEFINITIONS[sortBy]
    const sortedRows = [...rankingRows].sort((left, right) =>
        sortDefinition.compare(left, right, sortDirection),
    )
    const sortContext: SortContext = {
        rowsCount: sortedRows.length,
    }

    const displayRows = sortedRows.map((row, rowIndex) => ({
        ...row,
        displayRank: sortDefinition.getDisplayRank(
            row,
            rowIndex,
            sortDirection,
            sortContext,
        ),
    }))

    if (!summaryRow) {
        return displayRows
    }

    return [
        ...displayRows,
        {
            ...summaryRow,
            displayRank: null,
        },
    ]
}
